/* global Element */

const doc = window.document
const docElem = doc.documentElement

/**
 * Check if element matches selector
 * @param  {[type]} el       [description]
 * @param  {[type]} selector [description]
 * @return {[type]}          [description]
 */
export function matches(el, selector) {
  const p = Element.prototype
  const f =
    p.matches ||
    p.matchesSelector ||
    p.mozMatchesSelector ||
    p.msMatchesSelector ||
    p.webkitMatchesSelector ||
    p.oMatchesSelector ||
    function (s) {
      return (
        [].indexOf.call(
          (document || el.ownerDocument).querySelectorAll(s),
          this,
        ) !== -1
      )
    }

  return f.call(el, selector)
}

/**
 * Set URL parameter
 * @param { String } key
 * @param { String } value
 * @param { String } url
 */
export function setUrlParameter(key, value, url = window.location.href) {
  const re = new RegExp('([?&])' + key + '=.*?(&|$)', 'i')
  const separator = url.indexOf('?') !== -1 ? '&' : '?'

  if (url.match(re)) {
    if (value) {
      window.history.pushState(
        null,
        null,
        url.replace(re, '$1' + key + '=' + value + '$2'),
      )
    } else {
      window.history.pushState(null, null, url.replace(re, '$2'))
    }
  } else {
    window.history.pushState(null, null, url + separator + key + '=' + value)
  }
}

/**
 * Get URL parameter
 * @param  { String } name
 * @param  { String } url
 * @return { String }
 */
export function getUrlParameter(name, url = window.location.href) {
  name = name.replace(/[[]]/g, '\\$&')
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)', 'i')
  const results = regex.exec(url)

  if (!results) return null
  if (!results[2]) return ''

  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

/**
 * Slugify -
 * @param  {String} text
 * @return {String}
 */
export function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .replace(/(å)|(ä)/g, 'a')
    .replace(/(ö)/g, 'o')
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '') // Trim - from end of text
}

/**
 * Get closest DOM element up the tree that contains a class, ID, or data attribute
 * @param  {Node} elem The base element
 * @param  {String} selector The class, id, data attribute, or tag to look for
 * @return {Node} Null if no match
 */
export function getClosest(elem, selector) {
  const firstChar = selector.charAt(0)

  // Get closest match
  for (; elem && elem !== doc; elem = elem.parentNode) {
    // If selector is a class
    if (firstChar === '.') {
      if (elem.classList.contains(selector.substr(1))) {
        return elem
      }
    }

    // If selector is an ID
    if (firstChar === '#') {
      if (elem.id === selector.substr(1)) {
        return elem
      }
    }

    // If selector is a data attribute
    if (firstChar === '[') {
      if (elem.hasAttribute(selector.substr(1, selector.length - 2))) {
        return elem
      }
    }

    // If selector is a tag
    if (elem.tagName.toLowerCase() === selector) {
      return elem
    }
  }

  return false
}

/**
 * Get element data attributes in an object
 * @param  {Element} el
 * @return {Object} dataObject
 */
export function getElData(el) {
  if (el.dataset) {
    return JSON.parse(JSON.stringify(el.dataset))
  }

  const dataObject = {}
  const dataRegEx = /^data-(.+)$/

  nodesToArray(el.attributes).forEach((attribute) => {
    if (dataRegEx.test(attribute.nodeName)) {
      const key = attribute.nodeName.match(dataRegEx)[1]
      dataObject[key] = attribute.nodeValue
    }
  })

  return dataObject
}

/**
 * Creates DOM-elements from a string
 * @param  {[type]} string [description]
 * @return {[type]}        [description]
 */
export function stringToElements(string) {
  const div = doc.createElement('div')
  div.innerHTML = string

  if (div.childNodes.length > 1) {
    return div.childNodes
  }

  return div.firstChild
}

/**
 * Turns a list of nodes into an array
 * @param  { NodeList } nodeList
 * @return { Array }
 */
export function nodesToArray(nodeList) {
  return [...nodeList]
}

/**
 * Get viewport width
 * @return { Int }
 */
export function getViewportWidth() {
  return Math.max(docElem.clientWidth, window.innerWidth || 0)
}

/**
 * Get viewport height
 * @return { Int }
 */
export function getViewportHeight() {
  return Math.max(docElem.clientHeight, window.innerHeight || 0)
}

export function elementInViewport(el) {
  let top = el.offsetTop
  let left = el.offsetLeft
  const width = el.offsetWidth
  const height = el.offsetHeight

  while (el.offsetParent) {
    el = el.offsetParent
    top += el.offsetTop
    left += el.offsetLeft
  }

  return (
    top < window.pageYOffset + window.innerHeight &&
    left < window.pageXOffset + window.innerWidth &&
    top + height > window.pageYOffset &&
    left + width > window.pageXOffset
  )
}

/**
 * Check for https
 * @return { Boolean }
 */
export function isSecure() {
  return window.location.protocol === 'https:'
}

/**
 * svgRefresh
 * Fixes a webkit bug with svg elements using <use>
 */
export function svgRefresh() {
  nodesToArray(doc.querySelectorAll('use')).forEach((el) =>
    el.setAttribute('xlink:href', el.getAttribute('xlink:href')),
  )
}

/**
 * debounce
 * Delay execution of function
 */
export function debounce(fn, wait) {
  let timeout
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn.apply(this, arguments), wait || 1)
  }
}

/**
 * getRadius
 * @param  {[type]} x [description]
 * @return {[type]}   [description]
 */
function getRadius(x) {
  return (x * Math.PI) / 180
}

/**
 * [distanceBetween description]
 * @param  {[type]} x [description]
 * @param  {[type]} y [description]
 * @return {[type]}   in kilometers
 */
export function distanceBetween(x, y) {
  const radius = 6371
  const dLat = getRadius(y.lat - x.lat)
  const dLong = getRadius(y.lng - x.lng)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(getRadius(x.lat)) *
      Math.cos(getRadius(y.lat)) *
      Math.sin(dLong / 2) *
      Math.sin(dLong / 2)
  const b = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const c = radius * b

  return Math.round(c)
}

/**
 * Add class
 * @param { Element } el
 * @param { String } className
 */
export function addClass(el, className) {
  if (el.classList) {
    el.classList.add(className)
  } else {
    el.className += ' ' + className
  }
}

/**
 * Remove class
 * @param  {[type]} el        [description]
 * @param  {[type]} className [description]
 * @return {[type]}           [description]
 */
export function removeClass(el, className) {
  if (el.classList) {
    el.classList.remove(className)
  } else {
    el.className = el.className.replace(
      new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'),
      ' ',
    )
  }
}

/**
 * Toggle class
 * @param  { Element } el
 * @param  { String } className
 * @param  { Boolean } state     true = add, false = remove
 */
export function toggleClass(el, className, state) {
  state = isUndefined(state) ? !hasClass(el, className) : state

  if (el.classList) {
    el.classList.toggle(className, state)
  } else {
    if (state) {
      addClass(el, className)
    } else {
      removeClass(el, className)
    }
  }
}

/**
 * Check if element has class
 * @param  { Element }  el
 * @param  { String }  className
 * @return {Boolean}
 */
export function hasClass(el, className) {
  if (el.classList) {
    return el.classList.contains(className)
  }

  return new RegExp('(^| )' + className + '( |$)', 'gi').test(el.className)
}

/**
 * Check if object is undefined
 * @param  { Anything } obj
 * @return {Boolean}
 */
export function isUndefined(obj) {
  return typeof obj === 'undefined'
}

/**
 * Check if variable is string
 * @param  { Anything } variable
 * @return { Boolean }
 */
export function isString(variable) {
  return typeof variable === 'string'
}

/**
 * Remove element
 * @param  { Element } el
 */
export function removeElement(el) {
  el.parentNode.removeChild(el)
}
